import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["error", "dropzone", "form", "done", "fileInput", "fileList", "fileTemplate", "virtualFileInput"]
  static values = { maxFileSize: { type: Number, default: 50_000_000 } }

  connect() {
    this.createVirtualFileInput()

    this.dropzoneTarget.addEventListener("dragover", this.handleDragOver.bind(this))
    this.dropzoneTarget.addEventListener("drop", this.handleDrop.bind(this))
    this.virtualFileInput.addEventListener("change", this.handleFileInputChange.bind(this))
    this.element.closest("form").addEventListener("submit", this.handleFormSubmit.bind(this))
  }

  disconnect() {
    this.dropzoneTarget.removeEventListener("dragover", this.handleDragOver.bind(this))
    this.dropzoneTarget.removeEventListener("drop", this.handleDrop.bind(this))
    this.virtualFileInput.removeEventListener("change", this.handleFileInputChange.bind(this))
    this.element.closest("form").removeEventListener("submit", this.handleFormSubmit.bind(this))
  }

  createVirtualFileInput() {
    const input = document.createElement("input")
    input.type = "file"
    input.classList.add("hidden")
    input.setAttribute("data-files-target", "virtualFileInput")
    input.multiple = this.fileInputTarget.multiple

    this.element.appendChild(input)
    this.virtualFileInput = input
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  handleDrop(event) {
    event.preventDefault()
    const newFiles = event.dataTransfer.files
    this.addFiles(newFiles)
  }

  openDialog(event) {
    this.virtualFileInput.click()
  }

  handleFileInputChange(event) {
    const newFiles = event.target.files
    this.addFiles(newFiles)
    this.virtualFileInput.value = ""
  }

  filesChanged() {
    const files = this.fileInputTarget.files
    if (files.length === 0) {
      this.formTarget.classList.remove("hidden")
      if (this.hasDoneTarget) this.doneTarget.classList.add("hidden")
    } else if (files.length === 1 && !this.fileInputTarget.multiple) {
      this.formTarget.classList.add("hidden")
      if (this.hasDoneTarget) this.doneTarget.classList.remove("hidden")
      if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    } else {
      if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    }
  }

  addFiles(newFiles) {
    const existingFiles = this.fileInputTarget.multiple ? Array.from(this.fileInputTarget.files) : []
    const dataTransfer = new DataTransfer()

    existingFiles.forEach(file => dataTransfer.items.add(file))
    Array.from(newFiles).forEach(file => {
      if (!existingFiles.some(existingFile => existingFile.name === file.name)) {
        if (file.size <= this.maxFileSizeValue) {
          dataTransfer.items.add(file)
        } else {
          const maxMb = Math.round(this.maxFileSizeValue / 1_000_000)
          alert(`Je kunt geen bestanden groter dan ${maxMb}MB uploaden`)
        }
      }
    })

    this.fileInputTarget.files = dataTransfer.files
    this.filesChanged()
    this.updateFileList(dataTransfer.files)
  }

  updateFileList(files) {
    this.fileListTarget.innerHTML = ""
    Array.from(files).forEach((file, index) => {
      const fileItem = this.fileTemplateTarget.content.cloneNode(true)
      fileItem.querySelector(".file-name").textContent = file.name
      fileItem.querySelector(".file-size").textContent = this.formatFileSize(file.size)
      fileItem.querySelector(".file-remove").addEventListener("click", () => this.removeFile(index))
      this.fileListTarget.appendChild(fileItem)
    })
  }

  removeFile(index) {
    const existingFiles = Array.from(this.fileInputTarget.files)
    const dataTransfer = new DataTransfer()

    existingFiles.forEach((file, i) => {
      if (i !== index) {
        dataTransfer.items.add(file)
      }
    })

    this.fileInputTarget.files = dataTransfer.files
    this.filesChanged()
    this.updateFileList(dataTransfer.files)
  }

  handleFormSubmit(event) {
    const files = this.fileInputTarget.files
    if (files.length === 0) {
      event.preventDefault()
      this.errorTarget.classList.remove("hidden")
    }
  }

  formatFileSize(size) {
    const units = ["bytes", "KB", "MB", "GB", "TB"]
    let unitIndex = 0
    let readableSize = size

    while (readableSize >= 1024 && unitIndex < units.length - 1) {
      readableSize /= 1024
      unitIndex += 1
    }

    return `${readableSize.toFixed(2)} ${units[unitIndex]}`
  }
}
